// Miky Motorsport - app loop + state machine (M3): MENU / GARAGE / RACE
import * as THREE from 'three';
import { FIXED_DT, TICK_MS, MAX_FRAME_SKIP, ACTION, VIGNETTE, NEAR_MISS } from './constants.js';
import { shouldNearMiss } from './cameraMath.js';
import { RaceHUD } from './hud.js';
import { RaceManager, RACE_STATE } from './race.js';
import { MenuGarageUI } from './ui.js';
import { applyUpgrades, upgradeCost, computePI } from './garage.js';
import { getCar } from './data/cars.js';
import { composeRaceResult, racesEnteredDelta, assistsOffCount } from './economy.js';
import { saveProfile } from './storage.js';
import { findEvent, difficultyOf, CAREER } from './data/events.js';
import { recordEventResult, isSeriesComplete, medalTally, recordChampionshipResult, championshipPlayerRank, isFullField, isChampionshipWon, newlyWonChampionship, careerCompleted, pickEndRaceCelebration } from './career.js';
import { buildTrack } from './track.js';
import { getTrackDef, DEFAULT_TRACK, getBiome, TRACK_ORDER, refLapOf } from './data/tracks.js';
import {
  GhostRecorder, GhostPlayer, encodeGhost, decodeGhost,
  ghostToBase64, base64ToGhost, hashStr,
} from './ghost.js';
import { timeMedalFor, medalPrefix, fmtMs } from './timeMedals.js';
import { dailyChallenge, dateKey, updateDailyState, effectiveStreak, bestLapMsForTrack } from './daily.js';
import { buildRivalGhostFrames } from './rivalGhost.js';
import { sectorDeltas, pushHistory } from './sectors.js';
const APP = { MENU: 'MENU', GARAGE: 'GARAGE', RACE: 'RACE' };
const SHOW_POS = new THREE.Vector3(0, 1.06, 0);
const GHOST_DT = 1000 / 30;

// --- lokalni uloziste nejlepsiho ghostu per (trat, pocet kol, rezim) ---
// tt = Time Trial (oddeleny slot, aby se ghost neprelinal s vicevozovym zavodem stejne delky)
// mode = rezim zavodu (circuit/sprint/duel/...); ruzne rezimy na stejne trati+kolech maji
//   jine startovni timing (napr. pursuit hold 2.8 s), takze nesmi sdilet ghost.
function ghostKey(trackId, laps, mode, tt) { return `miky_ghost_${trackId}_${laps}_${mode}${tt ? '_tt' : ''}`; }
function loadBestGhost(trackId, laps, mode, tt) {
  try {
    const raw = localStorage.getItem(ghostKey(trackId, laps, mode, tt));
    if (!raw) return null;
    const o = JSON.parse(raw);
    const { meta, frames } = decodeGhost(base64ToGhost(o.data));
    if (meta.trackHash !== hashStr(trackId)) return null; // ghost z jine trate
    return { timeMs: o.timeMs, frames, dtMs: meta.dtMs || GHOST_DT };
  } catch { return null; }
}
function saveBestGhost(trackId, laps, mode, timeMs, frames, tt) {
  try {
    const buf = encodeGhost(frames, { trackHash: hashStr(trackId), lapMs: timeMs, dtMs: GHOST_DT });
    localStorage.setItem(ghostKey(trackId, laps, mode, tt), JSON.stringify({ timeMs, data: ghostToBase64(buf) }));
    return true;
  } catch { return false; }
}

export class Game {
  constructor(ctx) {
    this.sceneMgr = ctx.sceneMgr;
    this.world = ctx.world;
    this.track = ctx.track;
    this.camera = ctx.camera;
    this.input = ctx.input;
    this.particles = ctx.particles;
    this.profile = ctx.profile;
    this.audio = ctx.audio;
    this._lastBeepC = -1;
    this._prevSpeed = 0;
    this._prevPlayerPos = new THREE.Vector3();

    this.appState = APP.MENU;
    this.paused = false;
    this.race = null;
    this.hud = null;
    this.player = null;
    this.vehicle = null;
    this.showcase = null;

    this._pPos = new THREE.Vector3();
    this._cPos = new THREE.Vector3();
    this._iPos = new THREE.Vector3();
    this._pQ = new THREE.Quaternion();
    this._cQ = new THREE.Quaternion();
    this._iQ = new THREE.Quaternion();
    this._playerPos = new THREE.Vector3();
    this._playerQ = new THREE.Quaternion();
    this._wheelEuler = new THREE.Euler();
    this._tmpQ = new THREE.Quaternion();
    this._tmpV = new THREE.Vector3();

    this._accum = 0;
    this._last = 0;
    this._frameDt = FIXED_DT;
    this._raf = null;
    this._fpsSmooth = 60;
    this._controls = { throttle: 0, brake: 0, steer: 0, handbrake: false };
    this._loop = this._loop.bind(this);

    this._currentEvent = null; // probihajici event kariery (null = rychly zavod)
    this._ghostRec = null;     // nahravac trajektorie hrace (M5)
    this._ghostPlayer = null;  // prehravac ulozeneho nejlepsiho ghostu
    this._ghostObj = null;     // translucentni ghost auto ve scene
    this._pendingRivalGhost = null; // pripravene framy rivalova ghostu (denni vyzva)
    this._dailyChallenge = null;    // aktivni denni vyzva (J)
    this._ghostV = new THREE.Vector3();
    this._kickDir = new THREE.Vector3(); // A4: smer recoilu kamery pri narazu
    this._nearMissCd = 0;                // A4: cooldown near-miss svistu (s)
    this._ghostQ = new THREE.Quaternion();

    this.ui = new MenuGarageUI({
      onOpenGarage: () => this.openGarage(),
      onOpenCareer: () => this.openCareer(),
      onOpenSettings: () => this.openSettings(),
      onOpenTimeTrial: () => this.openTimeTrial(),
      onBackToMenu: () => this.openMenu(),
      onRace: () => this.startRace(),
      onStartEvent: (id) => this.startEvent(id),
      onStartTimeTrial: (trackId) => this.startTimeTrial(trackId),
      onStartDaily: () => this.startDaily(),
      onBuyUpgrade: (id) => this.buyUpgrade(id),
      onPaint: (color) => this.setPaint(color),
      onSelectCar: (id) => this.selectCar(id),
      onBuyCar: (id) => this.buyCar(id),
      onPreviewCar: (id) => this.previewCar(id),
      onToggleSetting: (group, key) => this.toggleSetting(group, key),
      onOpenPhoto: () => this.enterPhoto(),
      onPhotoCapture: () => this.capturePhoto(),
      onPhotoClose: () => this.exitPhoto(),
    });

    this._buildShowcase();
    this.openMenu();
  }

  start() { if (this._raf) return; this._last = performance.now(); this._raf = requestAnimationFrame(this._loop); }
  stop() { if (this._raf) cancelAnimationFrame(this._raf); this._raf = null; if (this.audio) this.audio.stopMusic(); }
  pause() { if (this.appState === APP.RACE) { this.paused = true; if (this.audio) this.audio.setSkid(0); } }
  resume() { if (!this.paused) return; this.paused = false; this._last = performance.now(); this._accum = 0; }
  togglePause() { this.paused ? this.resume() : this.pause(); }

  // --- spec hrace (base + upgrady + lak) ---
  playerSpec() {
    const base = getCar(this.profile.currentCar);
    const car = this.profile.cars[this.profile.currentCar];
    const s = applyUpgrades(base, car.upgrades);
    s.color = car.paint.color;
    return s;
  }

  // --- Showcase auto v garazi ---
  _buildShowcase(spec = this.playerSpec()) {
    if (this.showcase) {
      this.sceneMgr.remove(this.showcase.group);
      this.sceneMgr.disposeObject(this.showcase.group);
    }
    const built = this.sceneMgr.buildCar(spec);
    // ukotvi auto NA teren v miste showcase (jinak ho kopec u stredu sceny pohlti pod zem)
    const gy = (this.track && this.track.groundY) ? this.track.groundY(SHOW_POS.x, SHOW_POS.z) : 0;
    built.group.position.set(SHOW_POS.x, gy + (built.isModel ? 0.55 : SHOW_POS.y), SHOW_POS.z);
    this._placeProcWheels(built, spec);
    this.sceneMgr.add(built.group);
    this.showcase = { group: built.group, wheels: built.wheels, isModel: built.isModel };
  }
  // Rucni umisteni kol u proceduralniho auta (model ma kola zabudovana).
  _placeProcWheels(built, spec) {
    if (built.isModel) return;
    for (let i = 0; i < built.wheels.length; i++) {
      const p = spec.wheel.positions[i];
      built.wheels[i].position.set(p.x, p.y - 0.4, p.z);
    }
  }
  _showShowcase(v) {
    if (this.showcase) this.showcase.group.visible = v;
    // showcase = cisty backdrop: schovej scenerii (stromy by zakryvaly auto), pri zavode zpet
    if (this.track && this.track.setSceneryVisible) this.track.setSceneryVisible(!v);
  }

  openMenu() { this.appState = APP.MENU; this.input.setCaptureGameKeys(false); this.ui.showMenu(this.profile); this._showShowcase(true); if (this.audio) { this.audio.click(); this.audio.startMusic(); } }
  openGarage() { this.appState = APP.GARAGE; this._garageView = null; this.input.setCaptureGameKeys(false); this.ui.showGarage(this.profile, this.playerSpec()); this._showShowcase(true); if (this.audio) { this.audio.click(); this.audio.startMusic(); } }

  // Nahled jineho auta v garazi (vc. zamcenych) - ukaze 3D model + staty, bez koupe.
  // Kliknuti na aktualni auto preview zrusi (zpet na moje auto).
  previewCar(id) {
    if (!getCar(id)) return;
    this._garageView = (id === this.profile.currentCar) ? null : id;
    if (this.audio) this.audio.click();
    const spec = this._garageView ? this._viewSpec(id) : this.playerSpec();
    this._buildShowcase(spec);
    this.ui.updateGarage(this.profile, spec, this._garageView);
  }

  // Spec pro nahled: vlastnene auto s jeho upgrady, zamcene jako stock. Barva = jeho lak / default.
  _viewSpec(id) {
    const base = getCar(id);
    const car = this.profile.cars[id];
    const s = applyUpgrades(base, (car && car.owned) ? car.upgrades : {});
    s.color = (car && car.paint) ? car.paint.color : base.color;
    return s;
  }
  openCareer() {
    this.appState = APP.MENU;
    this.input.setCaptureGameKeys(false);
    const pi = computePI(this.playerSpec(), this.profile.cars[this.profile.currentCar].upgrades);
    this.ui.showCareer(this.profile, pi);
    this._showShowcase(true);
    if (this.audio) { this.audio.click(); this.audio.startMusic(); }
  }
  openSettings() {
    this.appState = APP.MENU;
    this.input.setCaptureGameKeys(false);
    this.ui.showSettings(this.profile);
    this._showShowcase(true);
    if (this.audio) this.audio.click();
  }
  openTimeTrial() {
    this.appState = APP.MENU;
    this.input.setCaptureGameKeys(false);
    this.ui.showTimeTrial(this.profile);
    this._showShowcase(true);
    if (this.audio) { this.audio.click(); this.audio.startMusic(); }
  }

  // --- Foto mod: volna orbit kamera kolem auta + snimek ---
  enterPhoto() {
    this._photo = true;
    if (this._photoAz == null) { this._photoAz = 0.7; this._photoEl = 0.3; this._photoDist = 7; }
    this._showShowcase(true);
    this.ui.showPhoto();
    this._bindPhotoControls();
    if (this.audio) this.audio.click();
  }
  exitPhoto() {
    this._photo = false;
    this._unbindPhotoControls();
    this.openGarage();
  }
  capturePhoto() {
    this.sceneMgr.render(this.camera.cam, this._frameDt); // cerstvy snimek
    try {
      const url = this.sceneMgr.canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url; a.download = 'miky-motorsport-foto.png';
      document.body.appendChild(a); a.click(); a.remove();
      if (this.audio) this.audio.beep(true);
    } catch (e) { console.warn('foto capture fail', e); }
  }
  _bindPhotoControls() {
    const canvas = this.sceneMgr.canvas;
    let dragging = false, lx = 0, ly = 0;
    this._photoHandlers = {
      down: (e) => { dragging = true; lx = e.clientX; ly = e.clientY; },
      move: (e) => {
        if (!dragging) return;
        this._photoAz -= (e.clientX - lx) * 0.006;
        this._photoEl = Math.max(0.05, Math.min(1.4, this._photoEl + (e.clientY - ly) * 0.005));
        lx = e.clientX; ly = e.clientY;
      },
      up: () => { dragging = false; },
      wheel: (e) => { e.preventDefault(); this._photoDist = Math.max(3.2, Math.min(13, this._photoDist + e.deltaY * 0.01)); },
    };
    canvas.addEventListener('pointerdown', this._photoHandlers.down);
    window.addEventListener('pointermove', this._photoHandlers.move);
    window.addEventListener('pointerup', this._photoHandlers.up);
    canvas.addEventListener('wheel', this._photoHandlers.wheel, { passive: false });
  }
  _unbindPhotoControls() {
    if (!this._photoHandlers) return;
    const canvas = this.sceneMgr.canvas;
    canvas.removeEventListener('pointerdown', this._photoHandlers.down);
    window.removeEventListener('pointermove', this._photoHandlers.move);
    window.removeEventListener('pointerup', this._photoHandlers.up);
    canvas.removeEventListener('wheel', this._photoHandlers.wheel);
    this._photoHandlers = null;
  }

  // Time Trial: solo jizda na cas + proti vlastnimu ghostovi (3 kola).
  startTimeTrial(trackId) {
    this._currentEvent = null;
    this.switchTrack(trackId);
    const laps = 3;
    const pb = this.profile.timeTrials[`${trackId}_${laps}`]?.totalMs ?? null;
    this._beginRace({ numAI: 0, laps, timeTrial: true, ttPB: pb });
  }

  // Denni vyzva (J): deterministicka trat+rival ze dne; honis rivaluv ghost na cas.
  startDaily() {
    this._currentEvent = null;
    const ch = dailyChallenge(dateKey(new Date()), TRACK_ORDER, refLapOf,
      (id) => bestLapMsForTrack(this.profile.timeTrials, id));
    if (!ch) return;
    this._dailyChallenge = ch;
    this.switchTrack(ch.trackId);
    // rivaluv ghost = jizda po ose trate cilovym tempem (dohni ho!)
    const lapMs = ch.targetMs / ch.laps;
    this._pendingRivalGhost = buildRivalGhostFrames(
      this.track.centerline, lapMs, ch.laps, GHOST_DT, { groundY: this.track.groundY },
    );
    this._beginRace({ numAI: 0, laps: ch.laps, timeTrial: true, daily: true });
  }
  toggleSetting(group, key) {
    const s = this.profile.settings;
    if (group === 'assists') s.assists[key] = !s.assists[key];
    else s[key] = !s[key];
    saveProfile(this.profile);
    if (this.audio) this.audio.click();
    this.ui.updateSettings(this.profile);
  }

  buyUpgrade(id) {
    const car = this.profile.cars[this.profile.currentCar];
    const lvl = car.upgrades[id] || 0;
    const cost = upgradeCost(id, lvl);
    if (cost == null || this.profile.credits < cost) return;
    this.profile.credits -= cost;
    car.upgrades[id] = lvl + 1;
    saveProfile(this.profile);
    if (this.audio) this.audio.beep(true);
    this.ui.updateGarage(this.profile, this.playerSpec());
  }

  setPaint(color) {
    const car = this.profile.cars[this.profile.currentCar];
    car.paint.color = color;
    saveProfile(this.profile);
    this._buildShowcase();
    this.ui.updateGarage(this.profile, this.playerSpec());
  }

  // Prepnuti na vlastnene auto
  selectCar(id) {
    if (!this.profile.cars[id] || !this.profile.cars[id].owned) return;
    if (id === this.profile.currentCar) return;
    this.profile.currentCar = id;
    this._garageView = null; // po vyberu/koupi zpet na aktualni auto
    saveProfile(this.profile);
    if (this.audio) this.audio.click();
    this._buildShowcase();
    this.ui.updateGarage(this.profile, this.playerSpec());
  }

  // Koupe noveho auta -> vlastnene + prepnuti
  buyCar(id) {
    const base = getCar(id);
    if (!base || this.profile.cars[id]) return; // uz vlastni nebo neexistuje
    if (this.profile.credits < base.price) return;
    this.profile.credits -= base.price;
    this.profile.cars[id] = { owned: true, upgrades: {}, paint: { color: base.color } };
    this.profile.currentCar = id;
    this._garageView = null; // po vyberu/koupi zpet na aktualni auto
    saveProfile(this.profile);
    if (this.audio) this.audio.beep(true);
    this._buildShowcase();
    this.ui.updateGarage(this.profile, this.playerSpec());
  }

  // --- Zavod ---
  // Rychly zavod z garaze (stredni obtiznost, bez zaznamu do kariery).
  startRace() {
    this._currentEvent = null;
    // rychly zavod vzdy na vychozi trati - jinak by jel na trati z posledniho eventu
    // kariery (nedeterministicka trat + cizi ghost).
    this.switchTrack(DEFAULT_TRACK);
    this._beginRace({ numAI: 4 });
  }

  // Event kariery - obtiznost / kola / pocet AI / rival / trat dle definice.
  startEvent(eventId) {
    const found = findEvent(eventId);
    if (!found) return;
    const { event, series } = found;
    this._currentEvent = event;
    this.switchTrack(event.track || series.track || DEFAULT_TRACK);
    const diff = difficultyOf(event);
    this._beginRace({
      numAI: event.numAI ?? 4,
      laps: event.laps,
      difficulty: diff,
      rival: event.rival || null,
      mode: event.mode || 'circuit',
    });
  }

  // Prepnuti trati (dispose stare vc. fyzikalnich teles + scenerie, postaveni nove).
  switchTrack(id) {
    const def = getTrackDef(id);
    // biom aplikuj jen kdyz se skutecne meni (setri PMREM regeneraci; nechá HDRI pro meadow)
    if (this.sceneMgr._biomeKey !== def.biome) {
      this.sceneMgr.applyBiome(getBiome(def.biome));
      this.sceneMgr._biomeKey = def.biome;
    }
    if (this.track && this.track.id === def.id) return;
    if (this.track && this.track.dispose) this.track.dispose();
    this.track = buildTrack(this.world, this.sceneMgr.scene, def);
  }

  _beginRace(raceOpts) {
    // zachyt aktivni trat do opts (prepnuti trate je side effect v startEvent/startRace/
    // startTimeTrial pred _beginRace) - jinak restartRace nevi, na ktere trati hrat.
    this._lastRaceOpts = { ...raceOpts, trackId: this.track.id }; // pro restart
    if (this.audio) { this.audio.click(); this.audio.stopMusic(); }
    this._showShowcase(false);
    this.ui.hideAll();
    this._lastBeepC = -1;
    this._prevSpeed = 0;
    const baseSpec = getCar(this.profile.currentCar);
    this.race = new RaceManager(this.world, this.track, this.sceneMgr, baseSpec, {
      playerSpec: this.playerSpec(),
      ...raceOpts,
    });
    this.player = this.race.player;
    this.vehicle = this.player.vehicle;
    this._prevGear = this.vehicle.gear; // A2: bazalni prevod (zadny falesny punch na startu)
    // vychozi pozice hrace pro detekci teleportu (jinak by prvni frame od (0,0,0) hlasil skok)
    const sp0 = this.player.curr.pos;
    this._prevPlayerPos.set(sp0.x, sp0.y, sp0.z);
    // Aplikuj asistenty hrace dle nastaveni (AI maji vsechny zapnute)
    const a = this.profile.settings?.assists;
    if (a) this.vehicle.assists = { abs: !!a.abs, tcs: !!a.tcs, stability: !!a.stability };
    // Ideální stopa (driving aid)
    if (this.track.setIdealLine) this.track.setIdealLine(!!this.profile.settings?.idealLine);
    this.hud = new RaceHUD({ onContinue: () => { if (this.race && this.race.state === RACE_STATE.FINISHED) this.endRace(); } });
    if (this.camera.snap) this.camera.snap();
    this._setupGhost();
    this.appState = APP.RACE;
    // od ted potlacuj vychozi chovani hernich klaves (sipky/WASD/Space/Enter) -
    // mimo zavod zustava vypnuto, aby slo menu ovladat klavesnici (F-311).
    this.input.setCaptureGameKeys(true);
    this.paused = false;
    this._last = performance.now();
    this._accum = 0;
  }

  // --- Ghost (M5): nahravej hrace; prehraj ulozeny nejlepsi beh stejneho typu zavodu ---
  _setupGhost() {
    this._ghostRec = new GhostRecorder(GHOST_DT);
    this._ghostPlayer = null;
    this._teardownGhostObj();
    // Denni vyzva: honis rivaluv (synteticky) ghost. Jinak vlastni nejlepsi beh.
    let frames = null, dtMs = GHOST_DT, rival = false;
    if (this._pendingRivalGhost && this._pendingRivalGhost.length > 1) {
      frames = this._pendingRivalGhost; rival = true;
    } else {
      const best = loadBestGhost(this.track.id, this.race.laps, this.race.mode, this.race.timeTrial);
      if (best && best.frames.length > 1) { frames = best.frames; dtMs = best.dtMs; }
    }
    this._pendingRivalGhost = null; // jednorazove
    if (frames && frames.length > 1) {
      this._ghostPlayer = new GhostPlayer(frames, dtMs);
      // rivaluv ghost cervene tonovany (odlisi se od vlastniho ghostu)
      const ghostSpec = rival ? { ...this.playerSpec(), color: 0xff4055 } : this.playerSpec();
      const built = this.sceneMgr.buildCar(ghostSpec);
      built.group.traverse((o) => {
        if (!o.material) return;
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach((m) => { m.transparent = true; m.opacity = 0.3; m.depthWrite = false; });
      });
      this._placeProcWheels(built, ghostSpec);
      this.sceneMgr.add(built.group);
      this._ghostObj = built.group;
    }
  }

  _teardownGhostObj() {
    if (this._ghostObj) {
      this.sceneMgr.remove(this._ghostObj);
      this.sceneMgr.disposeObject(this._ghostObj);
      this._ghostObj = null;
    }
  }

  // Uloz nejlepsi ghost behu (jen po dokonceni a kdyz je cas lepsi nez ulozeny).
  // tt rozlisuje Time Trial slot od vicevozoveho zavodu stejne delky.
  _saveGhostIfBest(tt) {
    if (!this.player.finished || !this._ghostRec || this._ghostRec.count <= 1) return;
    const laps = this.race.laps, mode = this.race.mode, trackId = this.track.id;
    const timeMs = this.player.finishTime * 1000;
    const prev = loadBestGhost(trackId, laps, mode, tt);
    if (!prev || timeMs < prev.timeMs) saveBestGhost(trackId, laps, mode, timeMs, this._ghostRec.frames, tt);
  }

  // Uvolneni zavodu bez odmen (quit/restart)
  _disposeRace() {
    if (this.audio) this.audio.setSkid(0);
    this._ghostRec = null;
    this._ghostPlayer = null;
    this._pendingRivalGhost = null;
    this._dailyChallenge = null;
    this._teardownGhostObj();
    if (this.race) this.race.dispose();
    this.race = null; this.player = null; this.vehicle = null;
    const hudEl = document.getElementById('hud');
    if (hudEl) hudEl.innerHTML = '';
    this.hud = null;
    this.paused = false;
  }

  // Spolecny teardown po dokonceni zavodu (endRace/_endTimeTrial): uvolni ghost,
  // zavod, HUD a prestav showcase auto v garazi. (Lisi se od _disposeRace, ktery
  // resi quit/restart - bez showcase, s paused reset a null-checky.)
  _teardownRaceAndHud() {
    this._ghostRec = null;
    this._ghostPlayer = null;
    this._teardownGhostObj();
    this.race.dispose();
    this.race = null; this.player = null; this.vehicle = null;
    document.getElementById('hud').innerHTML = '';
    this.hud = null;
    this._buildShowcase();
  }

  // Pauza -> zpet do menu/kariery (bez odmen, bez zapisu)
  quitRace() {
    const wasEvent = !!this._currentEvent;
    this._currentEvent = null;
    this._disposeRace();
    this._buildShowcase();
    if (wasEvent) this.openCareer(); else this.openMenu();
  }

  // Pauza -> restart stejneho zavodu od startu
  restartRace() {
    const opts = this._lastRaceOpts;
    this._disposeRace();
    // denni vyzva: restartuj pres startDaily (znovu postavi rivaluv ghost + dnesni vyzvu)
    if (opts && opts.daily) { this.startDaily(); return; }
    if (opts) {
      // zajisti spravnou trat (mohla se mezitim zmenit) pred restartem
      if (opts.trackId) this.switchTrack(opts.trackId);
      this._beginRace(opts);
    }
  }

  endRace() {
    if (this.race && this.race.timeTrial) return this._endTimeTrial();
    if (this.audio) this.audio.setSkid(0);
    const pos = this.player.position;
    const ev = this._currentEvent;
    const diff = ev ? difficultyOf(ev) : null;
    // bonus za vypnute asistenty (0.08 za kazdy vypnuty)
    const off = assistsOffCount(this.profile.settings?.assists);
    // Slozeni odmeny + medaile - cista logika v economy.composeRaceResult.
    const reward = composeRaceResult(pos, this.player.finished, ev, diff, off);
    this.profile.credits += reward.credits;
    this.profile.xp += reward.xp;
    this.profile.stats.races += racesEnteredDelta(); // F-105: kazdy odjety zavod (i DNF)
    if (pos === 1) this.profile.stats.wins++;

    const medal = reward.medal;
    let careerDone = false;
    let champRank = null;
    let wonChampSeries = null; // serie, jejiz titul hrac PRAVE timto zavodem ziskal (nebo null)
    if (ev) {
      recordEventResult(this.profile, ev.id, pos);
      // sampionat: zaznamenej poradi vsech ridicu (body se scitaji napric celou serii).
      // Body se ale scitaji JEN z plnych poli - duel (2 auta) by dal jednomu soupere
      // body zadarmo a ostatni by nemeli zadne kolo (nerovny pocet kol -> zkresleny
      // sampionat). Duel zustava bezny event (medaile/odmena), do sampionatu se nepocita.
      const ec = findEvent(ev.id);
      if (ec && ec.series.championship && this.race) {
        const champResults = this.race.results();
        if (isFullField(champResults)) {
          // zachyt stav PRED zaznamem, aby slo detekovat prechod (titul ziskan prave ted)
          const wonBefore = isChampionshipWon(this.profile, ec.series);
          recordChampionshipResult(this.profile, ec.series, ev.id, champResults);
          champRank = championshipPlayerRank(this.profile, ec.series);
          // oslava jen na PRECHODU false->true (ne na kazdem dalsim zavodu uz vyhrane serie)
          if (newlyWonChampionship(wonBefore, this.profile, ec.series)) wonChampSeries = ec.series;
        }
      }
      // dokonceni kariery: vsechny serie na medaile, NEBO vyhra Grand Championship finale.
      careerDone = careerCompleted(CAREER.every((s) => isSeriesComplete(this.profile, s)), ev.finale && pos === 1);
    }
    saveProfile(this.profile);
    // Poradi obrazovek - cista rozhodovaci logika (pickEndRaceCelebration). Vitezna obrazovka
    // kariery ma prednost, ale soubezne ziskany titul sampionatu UZNA (F-201: jinak by vyhra
    // Grand Finále navzdy spolkla oslavu Grand titulu, protoze prechod je jednorazovy).
    const celebration = pickEndRaceCelebration({ careerDone, wonChampSeries });
    if (celebration.kind === 'winner') this._showWinnerScreen(ev, celebration.acknowledgeChampionship);
    else if (celebration.kind === 'champion') this._showChampionScreen(celebration.acknowledgeChampionship);
    else this._rewardToast(pos, reward, medal, champRank);

    // Ulozeni nejlepsiho ghostu (jen po dokonceni a kdyz je cas lepsi nez ulozeny)
    this._saveGhostIfBest(false);
    this._teardownRaceAndHud();
    // Po eventu zpet do kariery, po rychlem zavodu do garaze.
    if (ev) { this._currentEvent = null; this.openCareer(); }
    else this.openGarage();
  }

  // Konec Time Trialu / denni vyzvy: uloz vysledek, zpet do TT obrazovky (bez odmen/kariery).
  _endTimeTrial() {
    if (this.audio) this.audio.setSkid(0);
    const p = this.player, laps = this.race.laps, trackId = this.track.id;
    const daily = this._dailyChallenge;
    const isDaily = !!(this.race.daily && daily);
    let timeMedal = null;
    let sectorDelta = null;
    if (!isDaily) this._saveGhostIfBest(true); // denni: rivaluv ghost je syntheticky, vlastni neukladame
    if (isDaily) {
      // Denni vyzva: vzdy posun dnesni datum v profilu - i pri DNF, aby karta poznala, ze
      // dnesni vyzva uz byla zkousena (F-106). updateDailyState pri finishMs<=0 nezapise PB
      // ani vyhru/streak, jen orazitkuje date. Dokonceni preda skutecny cas.
      const totalMs = p.finished ? p.finishTime * 1000 : 0;
      this.profile.daily = updateDailyState(this.profile.daily, daily.dayStr, totalMs, daily.targetMs);
    } else if (p.finished) {
      const totalMs = p.finishTime * 1000;
      const key = `${trackId}_${laps}`;
      const bestLapMs = p.bestLap === Infinity ? null : p.bestLap * 1000;
      const prev = this.profile.timeTrials[key] || {};
      // B3: sektory nejlepsiho kola (s -> ms) + delta vs predchozi nejlepsi + historie PB
      const newSectors = Array.isArray(p.bestSectors) ? p.bestSectors.map((s) => Math.round(s * 1000)) : null;
      sectorDelta = (newSectors && Array.isArray(prev.sectors)) ? sectorDeltas(newSectors, prev.sectors) : null;
      const improved = !prev.totalMs || totalMs < prev.totalMs;
      this.profile.timeTrials[key] = {
        totalMs: improved ? totalMs : prev.totalMs,
        bestLapMs: improved ? bestLapMs : (prev.bestLapMs ?? null),
        sectors: (improved && newSectors) ? newSectors : (prev.sectors || newSectors || null),
        history: pushHistory(prev.history, totalMs, 20),
      };
      timeMedal = timeMedalFor(totalMs, refLapOf(trackId), laps);
    }
    // F-105: pocitej KAZDY odjety zavod (i DNF) - stejne pravidlo jako career endRace.
    // Drive se TT/denni DNF nepocitalo -> counter znamenal v kazdem rezimu neco jineho.
    this.profile.stats.races += racesEnteredDelta();
    saveProfile(this.profile);
    const finishMs = p.finished ? p.finishTime * 1000 : null;
    this._dailyChallenge = null;
    this._teardownRaceAndHud();
    if (isDaily) this._dailyResultToast(daily, finishMs);
    else if (finishMs != null) this._ttResultToast(finishMs, timeMedal, sectorDelta);
    this.openTimeTrial();
  }

  // Spolecne sestaveni reward-toastu: Miky avatar + obsah <span>, append do #ui a auto-remove.
  // innerSpan = HTML uvnitr <span> (volajici si slozi text/medaili). ms = doba zobrazeni.
  _showToast(innerSpan, ms) {
    const t = document.createElement('div');
    t.className = 'reward-toast';
    t.innerHTML = `<img class="toast-miky" src="./img/miky-avatar.jpg" alt="Miky" draggable="false">
      <span>${innerSpan}</span>`;
    document.getElementById('ui').appendChild(t);
    setTimeout(() => t.remove(), ms);
  }

  // Toast po Time Trialu: cas + pripadna casova medaile.
  _ttResultToast(finishMs, medal, sectorDelta = null) {
    const icon = medalPrefix(medal);
    const medalLine = medal ? `<small>Časová medaile: ${icon}${medal}</small>` : '<small>Bez časové medaile - zkus rychleji!</small>';
    // B3: delta sektoru vs predchozi nejlepsi (zelene = zlepseni, cervene = ztrata)
    let secLine = '';
    if (Array.isArray(sectorDelta) && sectorDelta.length) {
      const parts = sectorDelta.map((d, i) => {
        const s = (d / 1000);
        const cls = d < 0 ? 'sec-up' : 'sec-down';
        const sign = d <= 0 ? '' : '+';
        return `<span class="${cls}">S${i + 1} ${sign}${s.toFixed(2)}</span>`;
      });
      secLine = `<small class="sec-line">${parts.join(' ')}</small>`;
    }
    this._showToast(`${icon}Čas ${fmtMs(finishMs)}${medalLine}${secLine}`, 4400);
  }

  // Toast po denni vyzve: porazil jsi rivala? + serie.
  _dailyResultToast(ch, finishMs) {
    const d = this.profile.daily;
    const won = !!(d && d.wonToday);
    // Efektivni streak (ne syrove d.streak) - po prohre/novem dni nesmi ukazovat starou hodnotu (F-103).
    const streak = effectiveStreak(d, ch.dayStr);
    let body;
    if (finishMs == null) body = `<small>Nedojel jsi. Zkus to znovu!</small>`;
    else if (won) body = `<small>Porazil jsi rivala ${ch.rival}! 🔥 série ${streak} ${streak === 1 ? 'den' : 'dní'}</small>`;
    else {
      const diff = (finishMs - ch.targetMs) / 1000;
      body = `<small>${ch.rival} byl o ${diff.toFixed(2)} s rychlejší. Zítra znovu!</small>`;
    }
    const head = won ? '🏆 Denní výzva splněna!' : '⏱ Denní výzva';
    this._showToast(`${head}${body}`, 4600);
  }

  _rewardToast(pos, reward, medal, champRank = null) {
    const iconPrefix = medalPrefix(medal);
    const champLine = champRank ? `<small>🏆 Šampionát: průběžně ${champRank}.</small>` : '';
    this._showToast(`${iconPrefix}Dojel jsi ${pos}.<small>+${reward.credits} CR &middot; +${reward.xp} XP</small>${champLine}`, 4200);
  }

  // Spolecny vitezny overlay: obal .winner-overlay > .winner-box, zaviraci tlacitko +
  // klik na pozadi, append do #ui a beep. boxInner = HTML uvnitr .winner-box.
  _showOverlay(boxInner) {
    const o = document.createElement('div');
    o.className = 'winner-overlay';
    o.innerHTML = `<div class="winner-box">${boxInner}</div>`;
    const close = () => o.remove();
    o.querySelector('.winner-close').onclick = close;
    o.onclick = (e) => { if (e.target === o) close(); };
    document.getElementById('ui').appendChild(o);
    if (this.audio) this.audio.beep(true);
  }

  // Vitezna obrazovka po dohrani kariery / vyhre finale. Obrazek je data-driven
  // (event.winnerImage), default je univerzalni hero foto - per-svet varianty lze doplnit pozdeji.
  // ackChampionship = serie, jejiz titul hrac PRAVE timto zavodem ziskal (Grand Finále je
  // zaroven posledni bodovane kolo i finale kariery) - uznáme ji extra radkem, aby oslava
  // titulu nezmizela bez povsimnuti (F-201). null pri bezne vyhre kariery.
  _showWinnerScreen(ev, ackChampionship = null) {
    const tally = medalTally(this.profile);
    const totalEvents = CAREER.reduce((n, s) => n + s.events.length, 0);
    const allGold = tally.gold === totalEvents;
    const img = (ev && ev.winnerImage) || './img/miky-winner.jpg';
    const sub = allGold
      ? 'Všechno zlato - jsi <b>LEGENDA MIKY</b>! 🌟'
      : 'Skvělá jízda, Miky! Posbírej všechno zlato a staň se Legendou.';
    const champLine = ackChampionship
      ? `<div class="winner-sub">🏆 A k tomu titul šampiona série <b>${ackChampionship.name}</b>! 🎉</div>`
      : '';
    this._showOverlay(`
        <img class="winner-photo" src="${img}" alt="Vítěz" draggable="false">
        <div class="winner-title">🏆 ŠAMPION!</div>
        <div class="winner-sub">${sub}</div>${champLine}
        <div class="winner-medals">🥇 ${tally.gold} &nbsp; 🥈 ${tally.silver} &nbsp; 🥉 ${tally.bronze}
          &nbsp;<span class="winner-count">(${tally.total}/${totalEvents})</span></div>
        <button class="btn btn-primary winner-close">Pokračovat</button>`);
  }

  // Oslava ziskaneho titulu sampionatu (F-102) - spousti se jen na prechodu (hrac PRAVE
  // ovladl prubezne poradi po odjeti vsech bodovanych kol serie). Stejny overlay styl jako
  // _showWinnerScreen, ale per-serie (nazev serie + ze je sampionem). Nedvoji se s viteznou
  // obrazovkou kariery (ta ma prednost - viz endRace).
  _showChampionScreen(series) {
    const img = './img/miky-winner.jpg';
    this._showOverlay(`
        <img class="winner-photo" src="${img}" alt="Šampion" draggable="false">
        <div class="winner-title">🏆 TITUL ŠAMPIONA!</div>
        <div class="winner-sub">Ovládl jsi <b>${series.name}</b> - jsi <b>ŠAMPION</b> celé série! 🎉</div>
        <button class="btn btn-primary winner-close">Pokračovat</button>`);
  }

  // --- Loop ---
  _loop(ts) {
    // Naplanuj dalsi frame HNED na zacatku - early returny nize (restart/quit/endRace)
    // jinak preskoci reschedule a smycka umre (zamrznuti po restartu / nehybny showcase v garazi).
    this._raf = requestAnimationFrame(this._loop);
    const dtMs = Math.min(ts - this._last, TICK_MS * MAX_FRAME_SKIP);
    this._last = ts;
    this._frameDt = Math.max(dtMs, 1) / 1000;
    this.input.update(this._frameDt);

    if (this.appState === APP.RACE) {
      this._controls = this.input.getControls();
      if (this.input.justPressed(ACTION.CAMERA)) this.camera.cycleView();
      if (this.input.justPressed(ACTION.RESET)) this.race.resetCar(this.player);
      if (this.input.justPressed(ACTION.PAUSE)) this.togglePause();
      // Pauza: M = zpet do menu (bez odmen), Enter = restart zavodu
      if (this.paused && this.input.justPressed(ACTION.QUIT)) { this.quitRace(); return; }
      if (this.paused && this.input.justPressed(ACTION.RESTART)) { this.restartRace(); return; }
      if (this.race.state === RACE_STATE.FINISHED && this.input.justPressed(ACTION.REWIND)) { this.endRace(); return; }

      if (!this.paused) {
        this._accum += dtMs;
        let steps = 0;
        while (this._accum >= TICK_MS && steps < MAX_FRAME_SKIP) {
          this.update(FIXED_DT);
          this._accum -= TICK_MS;
          steps++;
        }
      }
      const alpha = this.paused ? 1 : this._accum / TICK_MS;
      this._renderRace(alpha);
    } else {
      this._renderShowcase();
    }

    this._fpsSmooth = this._fpsSmooth * 0.92 + (1000 / Math.max(dtMs, 1)) * 0.08;
  }

  update(dt) {
    this.race.update(dt, this._controls);
    // nahravej trajektorii hrace behem zavodu (M5)
    if (this._ghostRec && this.race.state === RACE_STATE.RACING) {
      const c = this.player.curr;
      this._ghostRec.sample(dt, c.pos, c.rot);
    }
    this._spawnSkid();
    this.particles.update(dt);
  }

  _renderRace(alpha) {
    for (const car of this.race.cars) {
      this._renderCar(car, alpha);
      if (car.isPlayer) { this._playerPos.copy(this._iPos); this._playerQ.copy(this._iQ); }
    }
    // A2: gear-shift punch - FOV squeeze + lehký shake + audio blip při změně převodu
    const gear = this.vehicle.gear;
    if (this._prevGear != null && gear !== this._prevGear && this.race.state === RACE_STATE.RACING) {
      this.camera.punchFov(-3);
      this.camera.shake(0.1);
      if (this.audio) this.audio.gearShift(gear > this._prevGear);
    }
    this._prevGear = gear;
    // akcni kamera (naklon/look-ahead dle rizeni) jen kdyz je zapnuta v nastaveni; jinak steer=0
    const camSteer = this.profile.settings?.actionCam ? this._controls.steer : 0;
    this.camera.update(this._playerPos, this._playerQ, this.vehicle.speedMs, this._frameDt, camSteer);
    this.sceneMgr.updateSun(this._playerPos);
    this._renderGhost();
    this.particles.writeInstances();
    // A5: vignette sili s rychlosti (pocit rychlosti) - pred renderem
    const vKmh = Math.min(Math.abs(this.vehicle.speedKmh) / VIGNETTE.REF_KMH, 1);
    this.sceneMgr.setSpeedVignette(VIGNETTE.BASE + vKmh * VIGNETTE.SPEED);
    this.sceneMgr.render(this.camera.cam, this._frameDt);
    this.hud.update(this.race, this.vehicle, this.paused);

    if (this.audio) {
      this.audio.updateEngine(this.vehicle.engineRpm, this._controls.throttle, !this.paused);
      if (this.race.state === RACE_STATE.COUNTDOWN) {
        const c = Math.ceil(this.race.countdown - 1);
        if (c !== this._lastBeepC && c >= 1 && c <= 3) { this.audio.beep(false); this._lastBeepC = c; }
      } else if (this._lastBeepC !== 0) { this.audio.countdownGo(); this._lastBeepC = 0; }
      const sp = Math.abs(this.vehicle.speedKmh);
      // Detekce teleportu (reset T / pad / zaseknuti / flip / pursuit) podle velkeho skoku
      // pozice za jeden frame - jinak by nahly propad rychlosti po resetu spustil falesny
      // "crash" zvuk + otres kamery. Pri teleportu crash preskoc a jen resync rychlosti.
      const pc = this.player.curr.pos;
      const dx = pc.x - this._prevPlayerPos.x, dz = pc.z - this._prevPlayerPos.z;
      const teleported = (dx * dx + dz * dz) > 64; // > 8 m skok = reset, ne realna jizda
      if (!teleported && this._prevSpeed - sp > 22) {
        const sev = this._prevSpeed - sp;
        this.audio.thud(Math.min(1, sev / 60));
        this.camera.shake(Math.min(0.6, sev / 90));
        // A4: smerovy recoil kamery (dozadu vuci autu) + gamepad haptika
        this._kickDir.set(0, 0, 1).applyQuaternion(this._playerQ).negate();
        this.camera.kick(this._kickDir, Math.min(1.0, sev / 70));
        this.input.rumble(Math.min(1, sev / 50), 0.4, 180);
      }
      // A4: near-miss - tesne minuti AI -> svist + maly FOV spike (cooldown proti spamu)
      this._nearMissCd = Math.max(0, this._nearMissCd - this._frameDt);
      if (this._nearMissCd === 0 && sp >= NEAR_MISS.MIN_KMH && this.race.state === RACE_STATE.RACING) {
        for (const car of this.race.cars) {
          if (car.isPlayer) continue;
          const ddx = car.curr.pos.x - pc.x, ddz = car.curr.pos.z - pc.z;
          if (shouldNearMiss(Math.hypot(ddx, ddz), NEAR_MISS.DIST, sp, NEAR_MISS.MIN_KMH)) {
            this.audio.whoosh();
            this.camera.punchFov(2);
            this._nearMissCd = NEAR_MISS.COOLDOWN;
            break;
          }
        }
      }
      this._prevSpeed = sp;
      this._prevPlayerPos.set(pc.x, pc.y, pc.z);
    }
  }

  _renderGhost() {
    if (!this._ghostObj || !this._ghostPlayer) return;
    const tMs = this.race.raceTime * 1000;
    if (this.race.state !== RACE_STATE.RACING || tMs > this._ghostPlayer.duration + 500) {
      this._ghostObj.visible = false;
      return;
    }
    const s = this._ghostPlayer.sampleAt(tMs);
    if (!s) { this._ghostObj.visible = false; return; }
    this._ghostObj.visible = true;
    this._ghostObj.position.set(s.pos.x, s.pos.y, s.pos.z);
    this._ghostObj.quaternion.set(s.quat.x, s.quat.y, s.quat.z, s.quat.w);
  }

  _renderCar(car, alpha) {
    const prev = car.prev, curr = car.curr;
    this._pPos.set(prev.pos.x, prev.pos.y, prev.pos.z);
    this._cPos.set(curr.pos.x, curr.pos.y, curr.pos.z);
    this._iPos.lerpVectors(this._pPos, this._cPos, alpha);
    this._pQ.set(prev.rot.x, prev.rot.y, prev.rot.z, prev.rot.w);
    this._cQ.set(curr.rot.x, curr.rot.y, curr.rot.z, curr.rot.w);
    this._iQ.slerpQuaternions(this._pQ, this._cQ, alpha);
    car.group.position.copy(this._iPos);
    car.group.quaternion.copy(this._iQ);
    // interaktivni brzdova svetla (rozsviti se pri brzdeni)
    if (car.brakeMat) {
      const braking = car.vehicle.controls.brake > 0.08;
      car.brakeMat.emissiveIntensity = braking ? 7.0 : 0.85;
    }
    if (car.isModel) {
      // model ma kola na pevnych pozicich - jen rotace (roll vsechna, steer predni)
      const w0 = curr.wheels[0];
      const roll = w0 ? w0.roll : 0;
      let steer = 0;
      for (const w of curr.wheels) { if (w && Math.abs(w.steer) > Math.abs(steer)) steer = w.steer; }
      for (const wh of car.wheels) {
        wh.node.rotation.set(wh.base.x + roll, wh.base.y + (wh.front ? steer : 0), wh.base.z);
      }
    } else {
      for (let i = 0; i < car.wheels.length; i++) {
        const w = curr.wheels[i];
        if (!w) continue;
        const wm = car.wheels[i];
        wm.position.set(w.cx, w.cy - w.susp, w.cz);
        this._wheelEuler.set(w.roll, w.steer, 0, 'YXZ');
        wm.quaternion.setFromEuler(this._wheelEuler);
      }
    }
  }

  _renderShowcase() {
    const c = this.showcase ? this.showcase.group.position : SHOW_POS; // skutecna pozice auta (vc. terenu)
    if (this._photo && this.showcase) {
      // foto mod: volna orbit kamera kolem auta, auto se netoci
      const tx = c.x, ty = c.y + 0.2, tz = c.z;
      const ce = Math.cos(this._photoEl), se = Math.sin(this._photoEl);
      const ca = Math.cos(this._photoAz), sa = Math.sin(this._photoAz);
      const d = this._photoDist;
      this.camera.cam.position.set(tx + d * ce * sa, ty + d * se, tz + d * ce * ca);
      this.camera.cam.lookAt(tx, ty, tz);
      if (this.camera.cam.fov !== 46) { this.camera.cam.fov = 46; this.camera.cam.updateProjectionMatrix(); }
    } else if (this.showcase) {
      this.showcase.group.rotation.y += this._frameDt * 0.45;
      if (this.camera.cam.fov !== 40) { this.camera.cam.fov = 40; this.camera.cam.updateProjectionMatrix(); }
      // hero zaber relativni k SKUTECNE pozici auta (ukotveno na teren); lookAt mirne nad stredem
      this.camera.cam.position.set(c.x + 3.8, c.y + 1.7, c.z + 5.4);
      this.camera.cam.lookAt(c.x, c.y + 0.25, c.z);
    }
    this.sceneMgr.updateSun(c);
    this.sceneMgr.render(this.camera.cam, this._frameDt);
    if (this.audio) this.audio.updateEngine(0, 0, false);
  }

  _spawnSkid() {
    const v = this.vehicle;
    if (!v) return;
    const speedKmh = Math.abs(v.speedKmh);
    const slip = v.rearSlipAmount();
    const handbrake = this._controls.handbrake;
    // intenzita smyku 0..1: skutecny skluz pneu (uz v zatackach na limitu) + rucni brzda
    let intensity = 0;
    if (speedKmh > 8) intensity = Math.min(1, Math.max(0, slip - 0.2) * 1.7 + (handbrake ? 0.55 : 0));
    // kontinualni pisko pneu dle intenzity + charakter dle povrchu (A3)
    if (this.audio) this.audio.setSkid(intensity, v.surfaceGrip);
    if (intensity <= 0.1) return;
    const curr = this.player.curr;
    this._tmpQ.set(curr.rot.x, curr.rot.y, curr.rot.z, curr.rot.w);
    for (const i of v.rearWheels) {
      const w = curr.wheels[i];
      this._tmpV.set(w.cx, w.cy - w.susp - w.radius * 0.6, w.cz).applyQuaternion(this._tmpQ);
      this._tmpV.x += curr.pos.x; this._tmpV.y += curr.pos.y; this._tmpV.z += curr.pos.z;
      this.particles.spawnSmoke(this._tmpV.x, this._tmpV.y, this._tmpV.z, intensity);
    }
  }

  debugState() {
    const d = {
      appState: this.appState, credits: this.profile.credits, xp: this.profile.xp,
      fps: Math.round(this._fpsSmooth),
    };
    if (this.appState === APP.RACE && this.race) {
      d.raceState = this.race.state;
      d.speedKmh = Math.round(this.vehicle.speedKmh);
      d.playerPos = this.player.position;
      d.playerLap = this.player.lap + (this.player.started ? 1 : 0);
      d.finished = this.player.finished;
    }
    return d;
  }
}
